import { detectInfringements } from '@repo/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tabs,
  TabsContent,
} from '@repo/ui'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { CalendarDays, Download, FileDown, Loader2 } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import type { DddPeriod } from '@/features/tachograph/utils/ddd-period'
import type { DddViewerTabKey } from '@/features/tachograph/utils/ddd-viewer-tabs'
import type { DddViewerFile } from '@repo/shared'

import PageHeader from '@/components/layout/page-header'
import { DddEventsTable } from '@/features/tachograph/components/ddd-events-table'
import { DddSignatureBadge } from '@/features/tachograph/components/ddd-signature-badge'
import { DddActivitiesTab } from '@/features/tachograph/components/ddd-viewer/ddd-activities-tab'
import { DddCompaniesTab } from '@/features/tachograph/components/ddd-viewer/ddd-companies-tab'
import { DddComplianceTab } from '@/features/tachograph/components/ddd-viewer/ddd-compliance-tab'
import { DddConditionsTab } from '@/features/tachograph/components/ddd-viewer/ddd-conditions-tab'
import { DddControlsTab } from '@/features/tachograph/components/ddd-viewer/ddd-controls-tab'
import { DddDayPanel } from '@/features/tachograph/components/ddd-viewer/ddd-day-panel'
import { DddDownloadsTab } from '@/features/tachograph/components/ddd-viewer/ddd-downloads-tab'
import { DddDriversTab } from '@/features/tachograph/components/ddd-viewer/ddd-drivers-tab'
import { DddEmptyTab } from '@/features/tachograph/components/ddd-viewer/ddd-empty-tab'
import { DddOverviewTab } from '@/features/tachograph/components/ddd-viewer/ddd-overview-tab'
import { DddPeriodBrush } from '@/features/tachograph/components/ddd-viewer/ddd-period-brush'
import { DddPlacesTab } from '@/features/tachograph/components/ddd-viewer/ddd-places-tab'
import { DddSpeedTab } from '@/features/tachograph/components/ddd-viewer/ddd-speed-tab'
import { DddTechnicalTab } from '@/features/tachograph/components/ddd-viewer/ddd-technical-tab'
import { DddVehiclesTab } from '@/features/tachograph/components/ddd-viewer/ddd-vehicles-tab'
import { DddViewerTabsBar } from '@/features/tachograph/components/ddd-viewer/ddd-viewer-tabs-bar'
import {
  useDownloadDddReport,
  useDownloadFiles,
  useGetParsedDdd,
} from '@/features/tachograph/hooks'
import { buildComplianceData } from '@/features/tachograph/utils/ddd-compliance'
import { formatDddGeneration } from '@/features/tachograph/utils/ddd-generation'
import {
  analysisSlot,
  buildDayIndex,
  infringementsInPeriod,
  normalizePeriod,
  scopeParsed,
} from '@/features/tachograph/utils/ddd-period'
import {
  computeTotalsFromSegments,
  formatDriverName,
  mapDddActivitiesToSegments,
} from '@/features/tachograph/utils/ddd-to-segments'
import {
  buildUsageRows,
  usageBounds,
  usageInPeriod,
} from '@/features/tachograph/utils/ddd-usage'
import {
  DDD_VIEWER_TAB_KEYS,
  getDddViewerTabs,
} from '@/features/tachograph/utils/ddd-viewer-tabs'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { formatDate, formatDateTime } from '@/lib/format'
import { localizedPageTitle } from '@/lib/page-title'

const NOT_YET_AVAILABLE_TABS = ['borders', 'cargo'] as const

const VIEWER_TYPES = ['vehicle', 'driver'] as const
type ViewerType = (typeof VIEWER_TYPES)[number]

const dayKey = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)

const viewerSearchParams = z.object({
  tab: z.enum(DDD_VIEWER_TAB_KEYS).optional().catch(undefined),
  from: dayKey,
  to: dayKey,
  /** Open day panel. */
  day: dayKey,
})

function isViewerType(value: string): value is ViewerType {
  return (VIEWER_TYPES as ReadonlyArray<string>).includes(value)
}

export const Route = createFileRoute(
  '/dashboard/tachograph-activities/viewer/$type/$fileId',
)({
  head: () => ({ meta: [{ title: localizedPageTitle('dddViewer') }] }),
  validateSearch: viewerSearchParams,
  beforeLoad: ({ params }) => {
    if (!isViewerType(params.type)) {
      throw notFound()
    }
  },
  component: DddViewerPage,
})

function DddViewerPage() {
  useDocumentTitle('dddViewer')
  const { t } = useTranslation('tachographViewer')
  const { type, fileId } = Route.useParams()
  const viewerType = type as ViewerType

  const { data, isLoading, error } = useGetParsedDdd(viewerType, fileId)

  if (isLoading || !data) {
    return <DddViewerSkeleton />
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('errors.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            {t('errors.parseFailed')}
          </p>
        </CardContent>
      </Card>
    )
  }

  return <DddViewerContent parsed={data} type={viewerType} />
}

function DddViewerContent({
  parsed,
  type,
}: {
  parsed: DddViewerFile
  type: ViewerType
}) {
  const { t } = useTranslation('tachographViewer')
  const { fileId } = Route.useParams()
  const reportMutation = useDownloadDddReport()
  const rawMutation = useDownloadFiles()
  const driverName = formatDriverName(parsed.driverCard)
  const search = Route.useSearch()
  const { tab } = search
  const navigate = Route.useNavigate()
  const timeZone = parsed.timeZone

  // Infringements need the whole file for weekly/fortnight context; the
  // period only filters which ones are shown.
  const infringements = useMemo(
    () =>
      detectInfringements(parsed.activities, {
        timeZone,
        slot: analysisSlot(parsed),
        periodFrom: parsed.period.from,
        periodTo: parsed.period.to,
        places: parsed.places,
      }),
    [parsed, timeZone],
  )
  const days = useMemo(
    () => buildDayIndex(parsed, timeZone, infringements),
    [parsed, timeZone, infringements],
  )
  const period = useMemo(
    () => normalizePeriod(days, { from: search.from, to: search.to }),
    [days, search.from, search.to],
  )
  const scoped = useMemo(
    () => scopeParsed(parsed, period, timeZone),
    [parsed, period, timeZone],
  )
  const periodDays = useMemo(
    () =>
      period
        ? days.filter((d) => d.date >= period.from && d.date <= period.to)
        : days,
    [days, period],
  )
  const periodInfringements = useMemo(
    () => infringementsInPeriod(infringements, period, timeZone),
    [infringements, period, timeZone],
  )
  // One pass feeds the Compliance tab, its badge and the overview chips.
  const compliance = useMemo(
    () =>
      buildComplianceData(periodInfringements, scoped.events, scoped.faults),
    [periodInfringements, scoped.events, scoped.faults],
  )

  // Stable callbacks: the memoized overview/brush/day panel only re-render
  // when their data changes, not on every URL search update.
  const setPeriod = useCallback(
    (next: DddPeriod | null) => {
      void navigate({
        search: (prev) => ({ ...prev, from: next?.from, to: next?.to }),
        replace: true,
      })
    },
    [navigate],
  )
  const onTabChange = useCallback(
    (next: string) => {
      const key = DDD_VIEWER_TAB_KEYS.find((k) => k === next)
      if (!key) return
      void navigate({
        search: (prev) => ({
          ...prev,
          tab: key === 'overview' ? undefined : key,
        }),
        replace: true,
      })
    },
    [navigate],
  )
  const openTab = useCallback(
    (key: DddViewerTabKey) => onTabChange(key),
    [onTabChange],
  )
  const setDay = useCallback(
    (date: string | null) => {
      void navigate({
        search: (prev) => ({ ...prev, day: date ?? undefined }),
        replace: true,
      })
    },
    [navigate],
  )
  const dayKeys = useMemo(() => days.map((d) => d.date), [days])
  const openDay = search.day && dayKeys.includes(search.day) ? search.day : null

  // Vehicle count comes from full-file sessions overlapping the period, so a
  // vehicle whose session spans the whole period still counts.
  const usageRows = useMemo(() => buildUsageRows(parsed), [parsed])
  const tabs = useMemo(() => {
    const inPeriod = usageInPeriod(usageRows, usageBounds(period, timeZone))
    const complianceCount =
      periodInfringements.length +
      compliance.usageErrors.length +
      compliance.anomalies.length
    return getDddViewerTabs(scoped).map((x) => {
      if (x.key === 'vehicles' && parsed.meta.kind === 'card') {
        return { ...x, count: inPeriod.length }
      }
      if (x.key === 'compliance') return { ...x, count: complianceCount }
      return x
    })
  }, [
    scoped,
    usageRows,
    period,
    timeZone,
    parsed.meta.kind,
    periodInfringements,
    compliance,
  ])
  // Unknown or hidden tab (e.g. `borders` on a Gen1 file) falls back to overview.
  const activeTab = tabs.some((x) => x.key === tab) && tab ? tab : 'overview'

  const segments = useMemo(
    () =>
      mapDddActivitiesToSegments(scoped.activities, {
        driverName,
        driverCardCode: parsed.driverCard?.cardNumber ?? null,
      }),
    [scoped.activities, parsed.driverCard, driverName],
  )

  const totals = useMemo(() => computeTotalsFromSegments(segments), [segments])

  const title =
    type === 'driver'
      ? (driverName ??
        parsed.driverCard?.cardNumber ??
        t('fallbackTitle.driver'))
      : (parsed.vehicle?.vrn ??
        parsed.vehicle?.vin ??
        t('fallbackTitle.vehicle'))

  const subtitle = parsed.meta.fileName ?? t('subtitle.fallback')

  return (
    <div className="space-y-5">
      <PageHeader
        title={title}
        subtitle={subtitle}
        compact
        titleBadge={
          <div className="flex shrink-0 items-center gap-2">
            <Badge variant="outline" className="text-[10px]">
              {formatDddGeneration(parsed.meta.generation)}
            </Badge>
            <DddSignatureBadge
              valid={parsed.meta.signatureValid}
              signature={parsed.meta.signature}
            />
            <span
              className="text-muted-foreground flex items-center gap-1 text-xs whitespace-nowrap tabular-nums md:text-sm"
              title={`${t('identification.periodFrom')}: ${formatDateTime(parsed.period.from)}\n${t('identification.periodTo')}: ${formatDateTime(parsed.period.to)}`}
            >
              <CalendarDays className="size-3.5" aria-hidden />
              {formatDate(parsed.period.from, 'dd.MM.yyyy')} –{' '}
              {formatDate(parsed.period.to, 'dd.MM.yyyy')}
            </span>
          </div>
        }
        ctaButton={
          <div className="grid w-full grid-cols-2 gap-2 sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              className="h-10 px-3 text-sm has-[>svg]:px-3 sm:h-8 sm:text-[13px]"
              disabled={rawMutation.isPending}
              onClick={() => rawMutation.mutate({ type, ids: [fileId] })}
            >
              {rawMutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Download className="size-3.5" />
              )}
              {t('downloadRaw')}
            </Button>
            <Button
              size="sm"
              className="h-10 border border-transparent px-3 text-sm has-[>svg]:px-3 sm:h-8 sm:text-[13px]"
              disabled={reportMutation.isPending}
              onClick={() => reportMutation.mutate({ type, id: fileId })}
            >
              {reportMutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <FileDown className="size-3.5" />
              )}
              {t('downloadReport')}
            </Button>
          </div>
        }
      />

      <Tabs value={activeTab} onValueChange={onTabChange} className="gap-5">
        <DddViewerTabsBar
          tabs={tabs}
          active={activeTab}
          kind={parsed.meta.kind}
        />

        <TabsContent value="overview">
          <DddOverviewTab
            parsed={parsed}
            scoped={scoped}
            period={period}
            days={days}
            periodDays={periodDays}
            violations={periodInfringements.length}
            usageErrors={compliance.usageErrors.length}
            anomalies={compliance.anomalies.length}
            totals={totals}
            driverName={driverName}
            onOpenDay={setDay}
            onOpenTab={openTab}
          />
        </TabsContent>
        <TabsContent value="activities">
          <DddActivitiesTab
            parsed={parsed}
            days={days}
            period={period}
            onOpenDay={setDay}
          />
        </TabsContent>
        <TabsContent value="vehicles">
          <DddVehiclesTab parsed={parsed} period={period} />
        </TabsContent>
        <TabsContent value="places">
          <DddPlacesTab places={scoped.places} />
        </TabsContent>
        <TabsContent value="events">
          {scoped.events.length === 0 ? (
            <DddEmptyTab reason="noRecords" />
          ) : (
            <DddEventsTable
              title={t('tabs.events')}
              kind="event"
              rows={scoped.events}
            />
          )}
        </TabsContent>
        <TabsContent value="faults">
          {scoped.faults.length === 0 ? (
            <DddEmptyTab reason="noRecords" />
          ) : (
            <DddEventsTable
              title={t('tabs.faults')}
              kind="fault"
              rows={scoped.faults}
            />
          )}
        </TabsContent>
        <TabsContent value="conditions">
          <DddConditionsTab
            conditions={scoped.specificConditions}
            generation={parsed.meta.generation}
          />
        </TabsContent>
        <TabsContent value="compliance">
          <DddComplianceTab
            data={compliance}
            infringements={periodInfringements}
            timeZone={timeZone}
            dayKeys={dayKeys}
            onOpenDay={setDay}
          />
        </TabsContent>
        <TabsContent value="controls">
          <DddControlsTab controls={scoped.controlActivities} />
        </TabsContent>
        <TabsContent value="downloads">
          <DddDownloadsTab
            downloads={scoped.vuDownloads}
            controls={scoped.controlActivities}
          />
        </TabsContent>
        <TabsContent value="speed">
          <DddSpeedTab parsed={parsed} days={days} period={period} />
        </TabsContent>
        <TabsContent value="drivers">
          <DddDriversTab sessions={scoped.vuCardSessions} />
        </TabsContent>
        <TabsContent value="companies">
          <DddCompaniesTab locks={scoped.companyLocks} />
        </TabsContent>
        <TabsContent value="technical">
          <DddTechnicalTab parsed={parsed} />
        </TabsContent>
        {NOT_YET_AVAILABLE_TABS.map((key) => (
          <TabsContent key={key} value={key}>
            <DddEmptyTab reason={key} />
          </TabsContent>
        ))}
      </Tabs>

      <DddPeriodBrush
        days={days}
        period={period}
        onChange={setPeriod}
        onOpenDay={setDay}
      />
      <DddDayPanel
        parsed={parsed}
        date={openDay}
        dayKeys={dayKeys}
        infringements={infringements}
        onChange={setDay}
      />
    </div>
  )
}

function DddViewerSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-20 w-full rounded-xl" />
      <Skeleton className="h-40 w-full rounded-xl" />
      <Skeleton className="h-24 w-full rounded-xl" />
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  )
}
